/**
 * useDock — remembers per-dock mode: 'expanded' | 'icons' | 'collapsed'
 * date_of_change: 2025-09-04
 * 
 * Provides persistent state management for dock/sidebar visibility modes.
 * State is synchronized with localStorage for persistence across sessions.
 */
import { useEffect, useState } from 'react';

type Mode = 'expanded' | 'icons' | 'collapsed';

interface UseDockOptions {
  persist?: boolean;
}

export function useDock(
  key: string,
  initial: Mode = 'expanded',
  { persist = true }: UseDockOptions = {}
) {
  const storageKey = `dock:${key}`;
  
  const [mode, setMode] = useState<Mode>(() => {
    // The top toolbar is primary BattleRoom navigation, so its caller can opt
    // out of restoring a previously hidden state while sidebars remain user-
    // persistent. date_of_change: 2026-10-03
    if (!persist) {
      return initial;
    }

    try {
      const stored = localStorage.getItem(storageKey) as Mode;
      return stored && ['expanded', 'icons', 'collapsed'].includes(stored) ? stored : initial;
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    if (!persist) {
      return;
    }

    try {
      localStorage.setItem(storageKey, mode);
    } catch (error) {
      console.warn('[useDock] Failed to save to localStorage:', error);
    }
  }, [storageKey, mode, persist]);

  return { mode, setMode };
}
